import { TunaFishBreakAnimation } from '../../animations/TunaFishBreakAnimation';
import { CollideEvent, CollideId } from '../../defs/collide';
import type { TunaFishBlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { InvalidEffect } from '../effects/InvalidEffect';
import { CollideHandler, ICollision } from './ICollision';

export class TunaFishCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: TunaFishBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: TunaFishBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap(event: CollideEvent) {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    const collider = event.collider;

    // Attack only if basic block collider
    if (
      collider?.type === 'block' &&
      collider.entity.c.block.props.type === 'basic'
    ) {
      await this.attack();
      return true;
    }
    return false;
  }

  private async resolveAttack(event: CollideEvent) {
    await this.attack();
    return false;
  }

  private async attack() {
    // Despawn block
    despawnBlockEntity(this.scene, this.entity);

    // Play break animation
    this.playBreakAnimation();
  }

  private async playBreakAnimation() {
    const entity = this.entity;
    const animation = new TunaFishBreakAnimation();
    // Spawn an overlay entity for the animation
    const overlay = spawnOverlayEntity(
      this.scene,
      entity.c.position.mapPosition,
      entity.c.block.props,
      () => animation,
      'foreground',
    );
    // Play the animation
    await animation.start();
    // Despawn the overlay entity
    despawnOverlayEntity(this.scene, overlay);
  }
}
